/**
 * PaintlyKit — Free gift with purchase.
 *
 * Keeps a single gift line (a product from the accessories collection, chosen in
 * Theme settings → Free gift with purchase) in sync with whether the cart currently
 * qualifies for it: on every order, or once an eligible subtotal threshold is met.
 *
 * The theme only ever ADDS/REMOVES the gift line — it cannot make it free. The store's
 * native Shopify automatic discount is what zeroes the price at checkout (see
 * docs/free-gift.md). Everything here is therefore reversible cart plumbing, deliberately
 * modelled on assets/cart-shipping-protection.js so the two cart controllers behave the
 * same way and can't fight each other:
 *   - one in-flight mutation at a time (`busy`), and never while a cart update started
 *     elsewhere is still settling (`externalUpdates`)
 *   - read the real cart, plan an absolute-quantity update from it, apply, then verify the
 *     plan came out empty rather than trusting the response
 *   - dispatch CartLinesUpdateEvent so the rest of the theme (and the protection module)
 *     sees our mutation like any other
 *
 * Amount handling: thresholds are entered in the store's default currency (AUD) and
 * converted client-side with `Shopify.currency.rate`, exactly like the cart progress bar —
 * cart prices are already in the buyer's presentment currency, only the threshold isn't.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * Where the buyer's own decision about the gift is remembered for the session.
 *
 * Both directions have to persist, not just the refusal. A claim used to live on the element as
 * a flag cleared in `finally`, which meant manual mode could never actually keep the gift: the
 * claim added it, the flag was cleared, and the very next reconcile — a page load, any cart
 * change — planned it straight back out because nothing said the buyer had asked for it.
 *
 * Stored as `<variantId>|<choice>` so a change of gift product invalidates it rather than
 * silently applying an old decision to a new gift.
 */
const CHOICE_KEY = 'pk_free_gift_choice';

/**
 * Line item property marking the line THIS module put in the cart.
 *
 * The gift is an ordinary sellable product — it sits in the accessories collection and in the
 * cart upsell — so a buyer can quite reasonably be buying two of it already when they cross the
 * threshold. Without a marker the module can't tell its own line from theirs, and "keep exactly
 * one" silently rewrites a paid quantity of 2 down to 1. Shopify keeps lines with different
 * properties separate, so adding with this property gives the gift its own line: the buyer's
 * two stay theirs and untouched, this one is the module's to hold at exactly one and to take
 * back when the cart stops qualifying.
 *
 * The leading underscore is Shopify's convention for a property hidden from the customer in the
 * cart and at checkout.
 */
const GIFT_PROPERTY = '_free_gift';

let busy = false;
let failures = 0;
let lastGiftPresent = null;
const externalUpdates = new Set();
const ownEvents = new WeakSet();

const widgets = () => [...document.querySelectorAll('pk-free-gift')];

/**
 * Works out what the cart should look like, from the cart as it actually is.
 *
 * Pure and dependency-free so tests/free-gift.test.mjs can exercise every qualifying and
 * de-qualifying case without a browser. Returns absolute quantities keyed by line key (or
 * by variant id for a line that doesn't exist yet), the same shape /cart/update.js takes.
 *
 * @param {object} cart - A /cart.js response.
 * @param {object} config
 * @param {string|number} config.productId - The gift product. Not what identifies the module's
 *   own line — the marker property does that — but kept so a widget with no product configured
 *   can be recognised as inert.
 * @param {string|number} config.variantId - The gift variant to keep.
 * @param {boolean} config.giftEnabled - Feature switched on in Theme settings.
 * @param {string} config.qualify - 'always' (every order) or 'minimum' (spend threshold).
 * @param {number} config.thresholdCents - Threshold ALREADY converted to the cart's currency.
 * @param {boolean} config.wanted - Whether the buyer should have the gift right now
 *   (auto mode and not declined, or a manual claim).
 * @param {string|number} [config.protectionProductId] - Shipping protection product, excluded
 *   from the qualifying subtotal.
 */
export function giftPlan(cart, config) {
  const { variantId, giftEnabled, qualify, thresholdCents, wanted } = config;
  const items = Array.isArray(cart?.items) ? cart.items : [];

  // Only lines this module added. The marker alone decides that — deliberately not "the marker
  // AND the currently configured gift product": change the gift in Theme settings and every
  // marked line already sitting in a live cart would otherwise stop being recognised, never be
  // removed, and go to checkout at full price. A line of the gift product WITHOUT the marker is
  // the buyer's own purchase and is never touched — not its quantity, not its existence.
  const isManaged = (item) => Boolean(item.properties && item.properties[GIFT_PROPERTY]);

  // Neither the free line nor a shipping-protection fee is merchandise the buyer spent money
  // on, so neither may help clear the gift's own threshold. Without this the gift would prop up
  // the subtotal that justifies it: a buyer at exactly $50 who removes a $10 item would still
  // read as qualifying on the strength of the gift alone. A pen the buyer actually paid for
  // does count, because they did.
  const counts = (item) => !isManaged(item) && String(item.product_id) !== String(config.protectionProductId);

  const eligibleSubtotal = items.reduce((sum, item) => (counts(item) ? sum + item.final_line_price : sum), 0);
  const merchandiseCount = items.reduce((n, item) => (counts(item) ? n + item.quantity : n), 0);
  const qualified =
    Boolean(giftEnabled) &&
    merchandiseCount > 0 &&
    (qualify === 'always' || eligibleSubtotal >= thresholdCents);

  const managed = items.filter(isManaged);
  const keep = qualified && wanted ? managed.find((item) => String(item.variant_id) === String(variantId)) : null;

  const updates = {};
  for (const line of managed) {
    // Absolute quantities, never deltas: a duplicate managed line, or one whose quantity was
    // bumped, is corrected by the same pass that would remove it.
    const quantity = line === keep ? 1 : 0;
    if (line.quantity !== quantity) updates[line.key] = quantity;
  }

  return {
    qualified,
    eligibleSubtotal,
    merchandiseCount,
    managedLines: managed.length,
    managedQuantity: managed.reduce((n, item) => n + item.quantity, 0),
    inCart: managed.length > 0,
    updates,
    // An add can't go through /cart/update.js — that endpoint takes quantities, not properties —
    // so it's reported separately and applied with /cart/add.js.
    add: Boolean(qualified && wanted && !keep),
  };
}

/**
 * Whether the buyer should have the gift right now, given how it's offered and what they've
 * decided. Auto mode gives it unless they turned it down; manual mode withholds it until they
 * ask, and — the part that used to be missing — keeps giving it once they have.
 *
 * @param {string} mode - 'auto' or 'manual'.
 * @param {string|null} choice - 'claimed', 'declined', or null if they haven't said.
 */
export function wantsGift(mode, choice) {
  return mode === 'auto' ? choice !== 'declined' : choice === 'claimed';
}

/** True when the cart still needs work to match the plan. */
function planIsSettled(plan) {
  return !plan.add && Object.keys(plan.updates).length === 0;
}

/**
 * Cheap pre-check against the server-rendered state, so an ordinary page load doesn't spend a
 * cart request confirming there's nothing to do. The dataset is re-rendered by the Section
 * Rendering API on every cart update, so it's a live view of the cart, just a coarser one:
 * it can't see a duplicate gift line or a quantity the buyer bumped. That's fine — those only
 * arise from a cart mutation, and every mutation reaches us as an external update, which goes
 * through `reconcile` and its authoritative `giftPlan` read either way.
 */
function datasetSuggestsChange(widget, config) {
  const data = widget.dataset;
  const subtotal = parseInt(data.eligibleSubtotalCents || '0', 10);
  const count = parseInt(data.merchandiseCount || '0', 10);
  // Recomputed rather than read from data-qualified: the threshold is stored in the store's
  // default currency, and for a buyer on another Markets currency only the client knows the
  // converted figure the server compared against the wrong one.
  const qualified =
    config.giftEnabled && count > 0 && (config.qualify === 'always' || subtotal >= config.thresholdCents);

  const lines = parseInt(data.giftLines || '0', 10);
  const quantity = parseInt(data.giftQuantity || '0', 10);

  // Quantity and line count, not just "is it there". This used to compare against a single
  // boolean, which meant a managed line sitting at quantity 5, or two duplicate managed lines,
  // both read as "in cart" against "should be in cart" and agreed — so no reconcile ran and
  // giftPlan's duplicate correction, though tested, was unreachable on the live path.
  if (!(qualified && config.wanted)) return lines > 0;
  return lines !== 1 || quantity !== 1;
}

function readChoice(variantId) {
  try {
    const raw = sessionStorage.getItem(CHOICE_KEY);
    if (!raw) return null;
    const separator = raw.lastIndexOf('|');
    if (separator < 0) return null;
    return raw.slice(0, separator) === String(variantId) ? raw.slice(separator + 1) : null;
  } catch {
    return null;
  }
}

function writeChoice(variantId, choice) {
  try {
    if (choice) sessionStorage.setItem(CHOICE_KEY, String(variantId) + '|' + choice);
    else sessionStorage.removeItem(CHOICE_KEY);
  } catch {
    /* Private mode / storage disabled — the choice just doesn't stick for this session. */
  }
}

function configFor(widget) {
  const data = widget.dataset;
  const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
  const choice = readChoice(data.variantId);
  return {
    productId: data.productId,
    variantId: data.variantId,
    protectionProductId: data.protectionProductId,
    // False only in the orphan-cleanup case: the feature was switched off while this cart
    // still held a gift line (see snippets/pk-free-gift.liquid).
    giftEnabled: data.giftEnabled === 'true',
    qualify: data.qualify,
    // Per-market thresholds (snippets/pk-free-gift.liquid) are already in the cart currency.
    thresholdCents: Math.round(parseInt(data.thresholdCents || '0', 10) * (data.thresholdLocal === 'true' ? 1 : rate)),
    wanted: wantsGift(data.mode, choice),
    rate,
  };
}

function setBusy() {
  const pending = busy || externalUpdates.size > 0;
  for (const widget of widgets()) {
    for (const button of widget.querySelectorAll('button')) button.disabled = pending;
    widget.setAttribute('aria-busy', String(busy));
  }
  document.documentElement.classList.toggle('pk-free-gift-pending', busy);
}

function showError(message) {
  for (const widget of widgets()) {
    const error = widget.querySelector('.pk-free-gift__error');
    if (!error) continue;
    error.textContent = message;
    error.hidden = !message;
  }
}

/**
 * Patches the "spend $X more" amount client-side. Liquid renders the widget's copy, but it
 * can't convert the AUD threshold into the buyer's presentment currency (classic Liquid has
 * no access to the live Markets rate), so the remaining amount is the one string the server
 * can only get right for the default currency.
 */
function paintRemaining(config, subtotalCents, targets = widgets()) {
  if (config.qualify !== 'minimum') return;
  const remaining = Math.max(0, config.thresholdCents - subtotalCents);
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const locale = document.documentElement.lang || (window.Shopify && window.Shopify.locale) || undefined;
  const amount = remaining / 100;
  let text;
  try {
    // narrowSymbol, matching the cart progress bar, so this reads "$5.00" alongside Liquid's
    // `money` output instead of "A$5.00" next to it.
    text = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(amount);
  } catch {
    try {
      text = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      text = amount.toFixed(2) + ' ' + currency;
    }
  }
  // Every widget on the page, not just the one that triggered this: the cart page carries the
  // drawer's copy of the widget as well, and a closed drawer showing a stale amount is what
  // the buyer sees the moment they open it.
  //
  // Rewrites the whole sentence from its own template rather than a nested amount span, so the
  // translated string stays a plain string on the server side and no markup has to survive the
  // `t` filter.
  for (const widget of targets) {
    const target = widget.querySelector('[data-gift-remaining]');
    const template = target?.dataset.giftLockedTemplate;
    if (!template) continue;
    const sentence = template.replace('[amount]', text);
    if (target.textContent !== sentence) target.textContent = sentence;
  }
}

async function readCart() {
  const response = await fetch(Theme.routes.cart_url + '.js', {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal: AbortSignal.timeout?.(15000),
  });
  if (!response.ok) throw new Error('Cart unavailable');
  const cart = await response.json();
  if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
  return cart;
}

async function renderCart(cart, sectionIds) {
  await Promise.all(
    sectionIds.map(async (id) => {
      const component = [...document.querySelectorAll('cart-items-component')].find((el) => el.dataset.sectionId === id);
      const options = { mode: component?.hasAttribute('data-drawer') ? 'hydration' : 'full' };
      if (cart.sections?.[id]) {
        await morphSection(id, cart.sections[id], options);
      } else {
        const html = await sectionRenderer.renderSection(id, { ...options, cache: false });
        if (!html) throw new Error('Cart rendering failed');
      }
    })
  );
}

/**
 * Brings the cart in line with the plan, if it isn't already. Safe to call on any signal —
 * it reads the real cart and no-ops when there's nothing to change, so page load, an
 * external cart update and a buyer's claim click all go through the same path.
 */
async function reconcile(widget) {
  if (busy || externalUpdates.size || failures >= 2) return;
  const config = configFor(widget);
  if (!config.productId || !config.variantId) return;

  busy = true;
  setBusy();
  showError('');

  const sectionIds = [
    ...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean)),
  ].slice(0, 5);
  const source = widget.closest('cart-items-component') || document;
  const deferred = CartLinesUpdateEvent.createPromise();
  deferred.promise.catch(() => {});
  let dispatched = false;
  let attempted = false;

  try {
    let cart = await readCart();
    const plan = giftPlan(cart, config);
    if (planIsSettled(plan)) {
      paintRemaining(config, plan.eligibleSubtotal);
      lastGiftPresent = plan.inCart;
      failures = 0;
      return;
    }

    const event = new CartLinesUpdateEvent({
      action: plan.add ? 'add' : 'remove',
      context: 'cart',
      lines: [{ merchandiseId: String(config.variantId), quantity: plan.add ? 1 : 0 }],
      promise: deferred.promise,
    });
    ownEvents.add(event);
    dispatched = true;
    source.dispatchEvent(event);

    const body = { sections: sectionIds, sections_url: window.location.pathname };

    // Removals and quantity corrections first, so a duplicate managed line is gone before a
    // fresh one goes in and there is never a moment with two of them in the cart.
    if (Object.keys(plan.updates).length) {
      attempted = true;
      const response = await fetch(Theme.routes.cart_update_url + '.js', {
        method: 'POST',
        signal: AbortSignal.timeout?.(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, updates: plan.updates }),
      });
      const result = await response.json();
      if (!response.ok || result.errors || !Array.isArray(result.items)) throw new Error('Cart update failed');
      cart = result;
    }

    // The add is its own request because /cart/update.js takes quantities and nothing else —
    // the marker property that makes this line the module's can only be set on the way in.
    if (plan.add) {
      attempted = true;
      // No '.js' appended here, unlike cart_update_url just above: snippets/scripts.liquid
      // publishes cart_add_url with the suffix already on it and cart_update_url without.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        signal: AbortSignal.timeout?.(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          items: [{ id: config.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }],
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error('Gift add failed');
      // /cart/add.js answers with what was added, not with the whole cart, so re-read rather
      // than reasoning about a partial response. It does return the rendered sections we asked
      // for, though, so carry those across — otherwise renderCart falls back to fetching every
      // cart section again, one request each, for markup already in hand.
      const sections = result.sections;
      cart = await readCart();
      if (sections) cart.sections = sections;
    }

    const remaining = giftPlan(cart, config);
    // A gift that's out of stock (or capped by an inventory rule) silently comes back short.
    // Don't loop on it: count it as a failure so the second attempt stops trying, and leave
    // the cart as Shopify actually left it.
    if (!planIsSettled(remaining)) throw new Error('Gift not updated');

    await renderCart(cart, sectionIds);
    paintRemaining(config, remaining.eligibleSubtotal);
    lastGiftPresent = remaining.inCart;
    failures = 0;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'free-gift', didError: false },
    });
  } catch (error) {
    failures += 1;
    if (attempted) {
      // The cart may well have changed even though we couldn't confirm it. Re-read and
      // re-render rather than retrying blind — a second speculative add is how buyers end
      // up with two gifts.
      try {
        const cart = await readCart();
        await renderCart(cart, sectionIds);
        lastGiftPresent = giftPlan(cart, config).inCart;
        if (dispatched) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, source: 'free-gift', didError: false },
          });
          dispatched = false;
        }
        if (planIsSettled(giftPlan(cart, config))) {
          failures = 0;
          return;
        }
      } catch {
        if (dispatched) deferred.reject(error);
        showError(widget.dataset.error);
        return;
      }
    }
    if (dispatched) deferred.reject(error);
    // Say so only when the buyer asked for this. A failed claim or decline leaves them
    // looking at a button that appeared to do nothing, which needs explaining; a background
    // auto-add that couldn't run is not something they initiated or can act on, and an error
    // about it would be the first they'd heard of the gift.
    if (widget.interacted) showError(widget.dataset.error);
  } finally {
    widget.interacted = false;
    busy = false;
    setBusy();
  }
}

class FreeGift extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    setBusy();
    const config = configFor(this);
    paintRemaining(config, parseInt(this.dataset.eligibleSubtotalCents || '0', 10), [this]);
    // Auto mode adds the gift the moment the cart earns it. Manual mode waits for a click to
    // add — but still has to give a gift back on its own once the cart stops qualifying,
    // which `datasetSuggestsChange` reports for either mode.
    if (datasetSuggestsChange(this, config)) queueMicrotask(() => reconcile(this));
    lastGiftPresent ??= this.dataset.giftInCart === 'true';
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const claim = event.target.closest('[data-gift-claim]');
    if (claim) {
      event.preventDefault();
      this.interacted = true;
      // Recorded before the request, not after: this is what makes the gift stay put once it
      // lands, and it overrides an earlier refusal so the button isn't inert for the session.
      writeChoice(this.dataset.variantId, 'claimed');
      failures = 0;
      reconcile(this);
    }
  };
}

/**
 * Reacts to cart changes made anywhere else (line removed, quantity changed, an accessory
 * added). Deferred until the other update has settled and repainted, so we plan against the
 * cart the buyer can actually see.
 */
function trackUpdate(event) {
  if (ownEvents.has(event) || !event.promise) return;
  externalUpdates.add(event.promise);
  setBusy();
  const settled = () => {
    externalUpdates.delete(event.promise);
    setBusy();
    const widget = widgets()[0];
    if (!widget) return;
    const present = widget.dataset.giftInCart === 'true';
    // The buyer removed the gift line themselves. Auto mode would put it straight back, which
    // reads as the cart refusing to let go of it, so remember the refusal for this session
    // (the "Add my gift" button is still there if they change their mind). Recorded in manual
    // mode too, where it clears an earlier claim so the same thing doesn't happen there.
    if (lastGiftPresent === true && !present && widget.dataset.qualified === 'true') {
      writeChoice(widget.dataset.variantId, 'declined');
    }
    lastGiftPresent = present;
    failures = 0;
    if (datasetSuggestsChange(widget, configFor(widget))) reconcile(widget);
    else paintRemaining(configFor(widget), parseInt(widget.dataset.eligibleSubtotalCents || '0', 10));
  };
  event.promise.then(() => requestAnimationFrame(settled), settled);
}

for (const type of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
  document.addEventListener(type, trackUpdate);
}

// While our own mutation is in flight the cart on screen is a moment out of date; let the
// protection module's identical guard and this one both keep hands off it.
function guardPending(event) {
  if (!busy) return;
  const target = event.target;
  if (target.closest('cart-items-component, [name="checkout"], form[action*="/cart/"]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
document.addEventListener('click', guardPending, true);
document.addEventListener('submit', guardPending, true);

document.documentElement.classList.add('pk-free-gift-ready');
if (!customElements.get('pk-free-gift')) customElements.define('pk-free-gift', FreeGift);
